import React from "react";
import {AppBar, Toolbar, Typography, Link, Button, Popper, Grow, ClickAwayListener, Paper, MenuList, MenuItem} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import MapModal from "../components/MapModal";
import IntroModal from "../components/IntroModal";
import CopyUrlBtn from "../components/CopyUrlBtn";
import Project1_TechnicalSummary from "../images/Project1_TechnicalSummary.pdf";
import CountryRoad from "../images/country.jpg";
import UG1 from "../images/UG1-ag-land.jpg";
import UG2 from "../images/UG2-connectivity.jpg"
import UG3 from "../images/UG3-pvr.jpg";
import UG4 from "../images/UG4-rank.jpg";
import UG5 from "../images/UG5-county.jpg";

//Icons
import MenuIcon from "@material-ui/icons/Menu";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import PublicIcon from "@material-ui/icons/Public";
import InfoIcon from "@material-ui/icons/Info";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexGrow: 1,
    zIndex: 50
  },
  paper: {
    marginRight: theme.spacing(2),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
    fontSize: "24px",
  },
  beta: {
    color: "#DEF000",
    align: "left",
  },
  MenuList: {
    padding: "18px",
    paddingLeft: "0px",
    paddingRight: "5px",
    margin: "-7px",
    fontSize: "16px",
  },
}));

export default function Navbar() {
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <div className={classes.root}>
      <AppBar position="static">
        <Toolbar>
          <div>
            <Button
              ref={anchorRef}
              aria-controls={open ? "menu-list-grow" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
              color="inherit"
              edge="start"
              className={classes.menuButton}
              aria-label="open drawer"
            >
              <MenuIcon />
            </Button>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === "bottom" ? "center top" : "center bottom",
                  }}
                >
                  <Paper
                    style={{
                      border: "7px solid #CED8D7",
                    }}
                  >
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList
                        autoFocusItem={open}
                        id="menu-list-grow"
                        onKeyDown={handleListKeyDown}
                        className={classes.MenuList}
                      >
                        <MenuItem onClick={handleClose} disabled>
                          <Link color="inherit" href="/project2">
                            <ExitToAppIcon
                              style={{ color: "rgb(246, 122, 55)" }}
                            />
                            &nbsp; Connectivity on Agricultural Lands
                          </Link>
                        </MenuItem>
                        <MenuItem onClick={handleClose}>
                          <Link color="inherit" href="/project2">
                            <ExitToAppIcon
                              style={{ color: "rgb(246, 122, 55)" }}
                            />
                            &nbsp; Anthropogenic Impacts
                          </Link>
                        </MenuItem>
                        <MenuItem onClick={handleClose}>
                          <Link color="inherit" href="/project3">
                            <ExitToAppIcon
                              style={{ color: "rgb(246, 122, 55)" }}
                            />
                            &nbsp; Monitoring Forest Change
                          </Link>
                        </MenuItem>
                        <MenuItem onClick={handleClose}>
                          <Link
                            color="inherit"
                            href="https://analytics-lab.org/ecosystemmonitoring/"
                          >
                            <PublicIcon
                              style={{ color: "rgb(246, 122, 55)" }}
                            />
                            &nbsp; Analytics Lab + AI for Earth
                          </Link>
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>

          
          <CopyUrlBtn />

          <MapModal
            align="center"
            ModalTitle={"User Guide"}
            ModalBody={
              <div>
                <h5>Background</h5>
                <p>
                Agricultural lands (croplands, pasture, and rangelands) make up close to half of the land area of the lower 48 United States and therefore have a crucial role to play in supporting connectivity between populations of native species. While intensive farming and habitat fragmentation associated with some agricultural practices represent major threats to native species and their ability to move, conservation-focused management of agricultural lands (e.g., planting grassland or forest strips, diversification of crops) can reduce these barriers to movement and support the flow of organisms across agricultural landscapes. The U.S. Department of Agriculture (USDA) allocates billions of dollars each year to ag land conservation programs, but information is limited on where to target these funds to maximize benefits for biodiversity, and many landowners may not realize that their lands are eligible for conservation-focused financial incentives.
                </p>
                <p>
                This interactive map explores the potential for agricultural lands across the U.S. to contribute to ecological connectivity (i.e., the movement of species and genes across the landscape) and identifies potential conservation opportunities on agricultural lands based on their contribution to both connectivity and food production. Here we step through the major features of the map and demonstrate how to use this tool to prioritize conservation actions on agricultural lands, both locally and at the national scale.
                </p>
                <h5>Explore connectivity on agricultural lands</h5>
                <p>While agricultural lands such as cropland and pasture typically support less movement of organisms than surrounding natural landscapes, connectivity through these working lands can in some cases be substantial, providing important linkages between areas of high quality habitat. This map allows you to view the types of agricultural land cover that occur in a given area and the degree of connectivity through those agricultural lands and surrounding landscapes. Data on agricultural land cover were produced for the American Farmland Trust’s <a href="https://csp-fut.appspot.com/">Farms Under Threat</a> analysis.
                </p>
                <img src={UG1} width="600" height="400"></img>
                <Typography variant="subtitle2">An example landscape in central Missouri characterized by a mix of agriculture and natural areas. Both agricultural land cover type (left panel) and satellite imagery (right panel) are shown.</Typography>
                <div style={{backgroundColor: "white", height: "14px", color: "transparent"}}><p>filler text</p></div>

                <img src={UG2} width="600" height="400"></img>
                <Typography variant="subtitle2">Connectivity across the same mixed agricultural and natural landscape in central Missouri.</Typography>
                <div style={{backgroundColor: "white", height: "14px", color: "transparent"}}><p>filler text</p></div>

                <h5>Explore agricultural land productivity, versatility, and resilience (PVR)</h5>
                <p>PVR is an index of agricultural land quality developed by the American Farmland Trust and Conservation Science Partners that estimates the suitability of a given location for long-term food production based on soil characteristics, growing season length, and land cover type (for more information on PVR, see the American Farmland Trust’s Farms Under Threat analysis). With this map, you can explore the PVR value of agricultural lands in your region or across the U.S.</p>
                <img src={UG3} width="600" height="400"></img>
                <Typography variant="subtitle2">Productivity, versatility, and resilience (PVR) mapped across the example landscape in central Missouri.</Typography>
                <div style={{backgroundColor: "white", height: "14px", color: "transparent"}}><p>filler text</p></div>

                <h5>Identify conservation opportunities based on connectivity and PVR rankings</h5>
                <p>By combining connectivity and PVR, this map identifies several categories of conservation opportunity on agricultural lands. The Connectivity and PVR Rankings layer classifies all ag land locations as low, medium, or high for both connectivity and PVR (see matrix in bottom left), which can then inform the type of conservation action that may be suitable for that area. For instance, locations that have high values of both connectivity and PVR (shown in purple) may be strong candidates for programs that incentivize wildlife-friendly farming practices (e.g., the USDA’s <a href="https://www.nrcs.usda.gov/wps/portal/nrcs/main/national/programs/financial/csp/">Conservation Stewardship Program</a>) given their importance for both species movement and food production. Locations with high connectivity value and low PVR (dark blue) may be targets for removal from production and for ecological restoration to bolster their potential to support wildlife habitat and the movement of organisms (e.g., through the USDA’s <a href="https://www.fsa.usda.gov/programs-and-services/conservation-programs/conservation-reserve-program/">Conservation Reserve Program</a>). Areas in yellow represent high-quality agricultural lands with relatively limited value for connectivity and may be good candidates for programs that keep lands in production and protect against conversion to urban or residential development (e.g., the USDA’s <a href="https://www.nrcs.usda.gov/wps/portal/nrcs/main/national/programs/easements/acep/">Agricultural Conservation Easement Program</a>). </p>
                <img src={UG4} width="600" height="400"></img>
                <Typography variant="subtitle2">Connectivity and PVR rankings for all locations on agricultural land across the example landscape in central Missouri. </Typography>
                <div style={{backgroundColor: "white", height: "14px", color: "transparent"}}><p>filler text</p></div>

                <p>At the local level, farmers, land trusts, and advocates can use this conservation opportunity tool to explore the types of conservation-focused financial incentives that might make sense for the landscapes they work in and care about. At a larger scale, the County-Level Summaries layer facilitates the rapid identification of regions across the U.S. with substantial amounts of agricultural land in a given category for PVR and connectivity, which can help facilitate prioritization and allocation of conservation funds by the USDA and other organizations. For instance, the county layer shows a cluster of counties in central Virginia with large amounts of agricultural land in the ‘high connectivity-high PVR’ category (purple). Zooming in on this area then allows the user to see which tracts of agricultural land are in that ‘high-high’ category and thus may be good candidates for conservation-focused incentives.</p>

                <img src={UG5} width="600" height="400"></img>
                <Typography variant="subtitle2">Connectivity and PVR rankings summarized at the county level, where the color of a county corresponds to the most common ranking across all agricultural lands in that county. The detailed area shows connectivity and PVR rankings of agricultural lands for an area in central Virginia with large amounts of land in the ‘high connectivity-high PVR’ category.</Typography>
                <div style={{backgroundColor: "white", height: "14px", color: "transparent"}}><p>filler text</p></div>


                <p>
                  Download the technical summary and methods 
                  <a href={Project1_TechnicalSummary} download="Connectivity_Across_Ag_Lands">
                    {" "}here.
                  </a>
                </p>
              </div>
            }
            modalImage={CountryRoad}
          >
            About this Map
          </MapModal>

          <Typography
            align="center"
            className={classes.title}
            variant="h6"
            noWrap
          >
            Connectivity Across U.S. Agricultural Lands{" "}
            {/* <span style={{ color: "#DEF000"}}>BETA</span> */}
          </Typography>

          <IntroModal 
          align="center"
          ModalTitle={"Welcome"}
          ModalBody={
            <div>
              <p style={{fontSize: "1rem"}}>
              Preserving movement pathways for native species and linkages between areas of intact natural habitat is key to mitigating the current biodiversity crisis. At the same time, protecting the highest quality agricultural lands is similarly critical to our long-term food security. This map explores the role of agricultural lands across the U.S. in supporting the movement of species and connecting high-quality habitats. 
              </p>
              <p style={{fontSize: "1rem"}}>
              We draw on new spatial data on agricultural land cover and management intensity to identify agricultural areas that are likely to provide important connectivity benefits to native species. By combining the connectivity value of agricultural lands with information on ag land quality and sustainability, this map can help landowners, conservation advocates, and agencies across the country identify conservation opportunities on agricultural lands that benefit both farmers and wildlife. Our {<InfoIcon />} provides details on how to use this tool for conservation planning.
              </p>
              <p style={{fontSize: "1rem"}}>
              A brief summary of our methods is available <a href="https://drive.google.com/file/d/1djWRZCmJeYjYAIc0D94ciD5aCeJKzGmP/view?usp=sharing">here</a>. 
              </p>
            </div>
            }>
            </IntroModal>


        </Toolbar>
      </AppBar>
    </div>
  );
}
